import React, { useState, useEffect } from "react";
import {
	Autocomplete,
	TextField,
	CircularProgress,
	Box,
	Alert,
	Button,
	FormHelperText,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Typography,
	Divider,
	Modal,
	FormGroup,
} from "@mui/material";
import "./ManageSiteMachines.css";
import customHttp from "../../services/customHTTP";
import Datagrid from "../../components/Datagrid/Datagrid";
import { useSite } from "../../context/siteContext";
import EditIcon from "@mui/icons-material/Edit";
import Swal from "sweetalert2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import { AddCircle, ChevronLeft, ChevronRight, Delete } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const ManageSiteMachines = () => {
	const { selectedSite, fetchSites } = useSite();
	const [customers, setCustomers] = useState([]);
	const [sites, setSites] = useState([]);
	const [error, setError] = useState("");
	const [formErrors, setFormErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [siteError, setSiteError] = useState("");
	const [sitesLoading, setSitesLoading] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [selectedSiteData, setSelectedSiteData] = useState(null);
	const [assignedMachines, setAssignedMachines] = useState([]);
	const [nonAssignedMachines, setNonAssignedMachines] = useState([]);
	const [newProvision, setNewProvision] = useState(false);
	const [machineSelected, setMachineSelected] = useState(null);
	const [submittingProvision, setSubmittingProvision] = useState(false);
	const [emptySites, setEmptySites] = useState(false);
	const [provisionFormData, setProvisionFormData] = useState({
		machine_name: "",
		machine_number: "",
		machine_serial_number: "",
		provision_date: "",
		status: "active",
		site_name: "",
		cus_name: "",
		machine_id: "",
		site_id: "",
		cus_id: "",
		site_number: "",
		cus_number: "",
	});
	const [validationErrors, setValidationErrors] = useState({});
	const [errorMessage, setErrorMessage] = useState("");
	const [selectedRows, setSelectedRows] = useState([]);

	// Handle change function for all fields
	const handleProvisionFormChange = (event) => {
		const { name, value } = event.target;
		setProvisionFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	useEffect(() => {
		const fetchCustomers = async () => {
			try {
				const response = await customHttp.get("/cus_sites/getcustomers");
				if (response.data.success) {
					// Filter customers where customer_name is not 'EMPTY', '', or null
					const filteredCustomers = response.data.data.filter(
						(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
					);
					setCustomers(filteredCustomers);
				} else {
					setError("Failed to fetch customers.");
				}
			} catch (err) {
				setError("Failed to fetch customers: " + err.message);
			} finally {
				setLoading(false);
			}
		};

		fetchCustomers();
	}, [selectedSite]);

	const handleShowSites = async (customer) => {
		setSitesLoading(true);
		try {
			const response = await customHttp.get(`/cus_sites/getsites/${customer.cus_id}`);
			if (response.data.success) {
				setSites(response.data.data);
				setSitesLoading(false);
				if (response.data.data.length === 0) {
					setEmptySites(true);
				} else {
					setEmptySites(false);
				}
			} else {
				setSiteError("Failed to fetch sites.");
			}
		} catch (err) {
			setSiteError("Failed to fetch sites: " + err.message);
		}
	};

	const handleEditSite = async (selectedSiteData) => {
		console.log(selectedSiteData);
		try {
			// Fetch all machines
			const respMachines = await customHttp.get("/machines");
			const allMachines = respMachines.data.data;

			const respMachinesProvisioned = await customHttp.post("/machines/bysiteid", {
				site_number: selectedSiteData.customer_site_number,
			});
			console.log(respMachinesProvisioned);
			const siteMachines = respMachinesProvisioned.data.data;
			const provisionedMachineIds = siteMachines.map((machine) => machine.machine_id);

			const filterMachines = allMachines
				.filter((machine) => provisionedMachineIds.includes(machine.machine_id))
				.map((machine) => {
					// Find the corresponding siteMachine for the current machine
					const siteMachine = siteMachines.find((sm) => sm.machine_id === machine.machine_id);

					// Add `sm_` prefixed keys to the machine
					if (siteMachine) {
						Object.keys(siteMachine).forEach((key) => {
							machine[`sm_${key}`] = siteMachine[key];
						});
					}
					return machine;
				});
			console.log(filterMachines);
			const provMachines = filterMachines.map((machine) => {
				return {
					machine_id: machine.machine_id,
					machine_name: machine.machine_name,
					sm_machine_product_name: machine.sm_machine_product_name,
					machine_number: machine.machine_number,
					sm_machine_status: machine.sm_machine_status,
					sm_machine_id: machine.sm_id,
				};
			});
			setAssignedMachines(provMachines);
			console.log(filterMachines);
			// Get machines that are not provisioned
			const nonProvisionedMachines = allMachines.filter(
				(machine) => !provisionedMachineIds.includes(machine.machine_id)
			);
			setNonAssignedMachines(nonProvisionedMachines);

			// Do something with filterMachines and nonProvisionedMachines as needed
		} catch (error) {
			console.error("Error fetching machines: ", error);
		}
	};

	const handleProvisionFormSubmit = async () => {
		setSubmittingProvision(true);
		const requiredFields = [
			{ field: "machine_name", label: "Machine Name" },
			{ field: "machine_serial_number", label: "Machine Serial Number" },
			{ field: "provision_date", label: "Provision Date" },
		];

		// Check for empty fields
		const emptyFields = requiredFields.filter(({ field }) => !provisionFormData[field]);

		if (emptyFields.length > 0) {
			// Highlight empty fields
			setValidationErrors(
				emptyFields.reduce((acc, { field }) => {
					acc[field] = true; // Set the error for this field
					return acc;
				}, {})
			);
			setSubmittingProvision(false);
			// Display error message
			setErrorMessage(`Please fill in the required fields: ${emptyFields.map((f) => f.label).join(", ")}`);
			return; // Stop submission
		}

		// Reset errors if validation passes
		setValidationErrors({});
		setErrorMessage("");

		const req_body = [
			{
				MachineNumber: provisionFormData.machine_number,
				MachineName: provisionFormData.machine_name,
				MachineSerialNo: provisionFormData.machine_serial_number,
				CustomerNumber: provisionFormData.cus_number,
				SiteNumber: provisionFormData.site_number,
			},
		];

		try {
			const responseProvision = await customHttp.post("/cus_sites/provisionmachines", { table_data: req_body });
			setSubmittingProvision(false);

			const results = responseProvision.data.results;
			const successResults = [];
			const errorResults = [];
			const existsResults = [];

			// Categorize results by status
			results.forEach((result) => {
				if (result.status === "inserted") {
					const data = {
						machine_id: result.data.machine_id,
						machine_name: result.data.machine_name,
						sm_machine_product_name: result.data.machine_product_name,
						machine_number: result.data.machine_number,
						sm_machine_status: result.data.machine_status,
						sm_machine_id: result.data.id,
					};

					setAssignedMachines((prev) => {
						return [...prev, data]; // This will append the new 'data' object to the previous array
					});
					successResults.push(result);
				} else if (result.status === "error") {
					errorResults.push(result);
				} else if (result.status === "exists") {
					existsResults.push(result);
				}
			});

			// Construct the success message
			let successMessage = "";
			if (successResults.length > 0) {
				successMessage += `<b>${successResults.length}</b> machine(s) successfully provisioned:<br>`;
				successResults.forEach((result, index) => {
					successMessage += `${index + 1}. Machine <b>${result.data.machine_name}</b> provisioned to <b>${
						provisionFormData.site_name
					}</b>.<br>`;
				});
			}

			// Construct the error message
			let errorMessage = "";
			if (errorResults.length > 0) {
				errorMessage += `<b>${errorResults.length}</b> machine(s) failed to provision due to errors:<br>`;
				errorResults.forEach((result, index) => {
					errorMessage += `${index + 1}. Machine <b>${result.data?.machine_name || "Unknown"}</b>: ${
						result.message
					}<br>`;
				});
			}

			// Construct the exists message
			let existsMessage = "";
			if (existsResults.length > 0) {
				existsMessage += `<b>${existsResults.length}</b> machine(s) already exist in the system:<br>`;
				existsResults.forEach((result, index) => {
					existsMessage += `${index + 1}. Machine <b>${result.data?.machine_name || "Unknown"}</b>: ${
						result.message
					}<br>`;
				});
			}

			// Display Swal
			Swal.fire({
				icon: successResults.length > 0 ? "success" : "info",
				title: "Provisioning Results",
				html: `${successMessage}${errorMessage}${existsMessage}`,
			});

			// Reset the form only if provisioning was successful
			if (successResults.length > 0) {
				setProvisionFormData({
					machine_name: "",
					machine_number: "",
					machine_serial_number: "",
					provision_date: "",
					status: "active",
					site_name: "",
					cus_name: "",
					site_id: "",
					cus_id: "",
					machine_id: "",
					site_number: "",
					cus_number: "",
				});
				setNewProvision(false);
				setMachineSelected(null);
			}

			console.log(responseProvision);
		} catch (error) {
			console.error(error);
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "Something went wrong while provisioning the machine. Please try again.",
			});
		}

		// Proceed with your API call or logic
	};

	const handleDeleteProvisions = async () => {
		console.log(selectedRows);
		try {
			const req_body = {
				sm_ids: selectedRows,
			};
			const response = await customHttp.post("/cus_sites/delete-provisions", req_body);
			console.log(response);
			if (response.data.success) {
				setAssignedMachines((prevList) => prevList.filter((row) => !selectedRows.includes(row.sm_machine_id)));
				Swal.fire({
					icon: "success",
					title: "Success",
					html: `Sucessfully deleted the Provisions`,
				});
			}
		} catch (error) {
			console.log(error);
		}
	};

	const handleRowSelect = (rows) => {
		setSelectedRows(rows);
	};

	const customButtons = [
		{
			label: "Add New Provision",
			icon: <AddCircle />,
			onClick: () => {
				setNewProvision(true);
			},
		},
		{
			label: "Delete Selected",
			icon: <Delete />,
			onClick: () => {
				handleDeleteProvisions();
			},
		},
	];

	const columns = [
		{ field: "machine_name", headerName: "Machine Name", flex: 1 },
		{
			field: "sm_machine_product_name",
			headerName: "Machine Serial #",
			width: 200,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => {
				const statusText =
					params.row.sm_machine_product_name === "EMPTY"
						? `<span style="font-size: 10px; color: #aaa">NA</span>`
						: params.row.sm_machine_product_name;
				return <span dangerouslySetInnerHTML={{ __html: statusText }} />;
			},
		},
		{ field: "machine_number", headerName: "Machine Number", width: 200, align: "center", headerAlign: "center" },
		{
			field: "sm_machine_status",
			headerName: "Machine Status",
			width: 200,
			align: "center",
			headerAlign: "center",
		},
	];

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}

	let label = "Select a customer to view locations";

	// Check if siteLoading is true

	if (emptySites) {
		label = "No Sites Found for Customer";
	} else if (!sitesLoading && sites.length === 0) {
		label = "Select Customer to view locations";
	} else if (sitesLoading) {
		label = "Loading...";
	} else if (!sitesLoading && sites.length > 0) {
		label = "Select Customer Site";
	}

	return (
		<div>
			<div
				style={{
					marginBottom: "20px",
					marginTop: "20px",
					display: "flex",
					flexDirection: "row",
					columnGap: "20px",
				}}>
				<Autocomplete
					options={customers}
					getOptionLabel={(option) => option.customer_name}
					value={selectedCustomer}
					onChange={(event, newValue) => {
						setSelectedCustomer(newValue);
						if (newValue) {
							handleShowSites(newValue);
						} else {
							setAssignedMachines([]);
							setNonAssignedMachines([]);
							setSelectedSiteData(null); // Clear selected site when customer is cleared
						}
					}}
					renderInput={(params) => <TextField {...params} label="Select a customer" variant="outlined" />}
					fullWidth
					size="small"
				/>

				<Autocomplete
					options={sites}
					getOptionLabel={(option) =>
						`${option.customer_site_name} - ${option.customer_site_number}` || "No sites available"
					}
					value={selectedSiteData}
					onChange={(event, newValue) => {
						// console.log(newValue);
						setSelectedSiteData(newValue);
						if (newValue) {
							handleEditSite(newValue);
						} else {
							setAssignedMachines([]);
							setNonAssignedMachines([]);
							setSelectedSiteData(null);
						}
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label={label}
							variant="outlined"
							placeholder={sitesLoading ? "Loading..." : "Select a customer to view locations"}
						/>
					)}
					fullWidth
					size="small"
					disabled={sitesLoading || sites.length === 0 || !selectedCustomer}
				/>
			</div>

			{selectedSiteData && (
				<>
					<Divider textAlign="center" sx={{ margin: "30px 0 0 0" }}>
						<Typography variant="h6">
							Provisioned Machines: <b>{selectedSiteData.customer_site_name}</b>
						</Typography>
					</Divider>
					<Datagrid
						data={assignedMachines}
						columns={columns}
						customButtons={customButtons}
						checkboxSelection={true}
						rowIdField="sm_machine_id"
						onSelect={handleRowSelect}
						sortBy={[{ field: "machine_name", sort: "asc" }]}
					/>

					<Modal open={newProvision} onClose={() => setNewProvision(false)}>
						<Box
							sx={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
								width: "fit-content",
								minWidth: "450px",
								bgcolor: "background.paper",
								boxShadow: 24,
								p: 4,
							}}>
							<Typography variant="h5" textAlign="center" fontWeight="bold">
								Add New Provision
							</Typography>
							<Box
								display="flex"
								flexDirection="column"
								gap={2}
								justifyContent="flex-start"
								alignItems="flex-start"
								style={{ width: "100%", minHeight: "350px", padding: "20px 30px" }}>
								<Autocomplete
									sx={{ width: "100%" }}
									options={nonAssignedMachines}
									getOptionLabel={(option) => `${option.machine_number} - ${option.machine_name}`}
									renderInput={(params) => (
										<TextField {...params} label="Select Machine" variant="outlined" size="small" />
									)}
									onChange={(event, value) => {
										if (value) {
											setProvisionFormData((prevData) => ({
												...prevData,
												machine_id: value.machine_id || "",
												machine_name: value.machine_name || "",
												machine_number: value.machine_number || "",
												site_id: selectedSiteData.site_id,
												cus_id: selectedCustomer.cus_id,
												site_name: selectedSiteData.customer_site_name,
												cus_name: selectedCustomer.customer_name,
												site_number: selectedSiteData.customer_site_number,
												cus_number: selectedCustomer.customer_number,
												provision_date: new Date().toISOString().split("T")[0],
											}));
											setMachineSelected(value);
										} else {
											setProvisionFormData({
												machine_name: "",
												machine_number: "",
												machine_serial_number: "",
												provision_date: "",
												status: "active",
												site_name: "",
												cus_name: "",
												site_id: "",
												cus_id: "",
												machine_id: "",
												site_number: "",
												cus_number: "",
											});
											setMachineSelected(null);
										} // Assign the selected machine object
									}}
									isOptionEqualToValue={(option, value) =>
										option.machine_number === value?.machine_number
									}
								/>

								{machineSelected && (
									<>
										<Typography variant="body1" fontWeight="bold">
											Machine Details:{" "}
										</Typography>
										<FormGroup row sx={{ width: "100%", columnGap: "10px" }}>
											<TextField
												label="Machine Name"
												size="small"
												fullWidth
												variant="outlined"
												name="machine_name"
												sx={{ flex: 1 }}
												value={provisionFormData.machine_name}
												onChange={handleProvisionFormChange}
												disabled={!machineSelected}
												error={!!validationErrors.machine_name} // Highlight if there's an error
												helperText={
													validationErrors.machine_name ? "Machine Name is required" : ""
												}
											/>

											{/* Machine Number */}
											<TextField
												label="Machine Number"
												size="small"
												fullWidth
												variant="outlined"
												name="machine_number"
												sx={{ flex: 1 }}
												value={provisionFormData.machine_number}
												onChange={handleProvisionFormChange}
												disabled
											/>

											{/* Machine Serial Number */}
											<TextField
												label="Machine Serial Number"
												size="small"
												fullWidth
												variant="outlined"
												name="machine_serial_number"
												sx={{ flex: 1 }}
												value={provisionFormData.machine_serial_number}
												onChange={handleProvisionFormChange}
												disabled={!machineSelected}
												error={!!validationErrors.machine_serial_number} // Highlight if there's an error
												helperText={
													validationErrors.machine_serial_number
														? "Machine Serial # is required"
														: ""
												}
											/>
										</FormGroup>

										<Typography variant="body1" fontWeight="bold">
											Provision Date:{" "}
										</Typography>
										<TextField
											label="Provision Date"
											type="date"
											size="small"
											fullWidth
											InputLabelProps={{ shrink: true }}
											variant="outlined"
											name="provision_date"
											value={provisionFormData.provision_date}
											onChange={handleProvisionFormChange}
											disabled={!machineSelected}
											error={!!validationErrors.provision_date} // Highlight if there's an error
											helperText={
												validationErrors.provision_date ? "Provision Date is required" : ""
											}
										/>
										<Typography variant="body1" fontWeight="bold">
											Provision For:{" "}
										</Typography>
										<FormGroup row sx={{ width: "100%", columnGap: "10px" }}>
											<TextField
												label="Customer Name"
												size="small"
												fullWidth
												variant="outlined"
												name="cus_name"
												sx={{ flex: 1 }}
												disabled
												value={provisionFormData.cus_name}
												onChange={handleProvisionFormChange}
											/>

											<TextField
												label="Site Name"
												size="small"
												fullWidth
												variant="outlined"
												name="site_name"
												sx={{ flex: 1 }}
												disabled
												value={provisionFormData.site_name}
												onChange={handleProvisionFormChange}
											/>
										</FormGroup>

										<Typography variant="body1" fontWeight="bold">
											Provision Status:{" "}
										</Typography>
										<FormControl size="small" fullWidth disabled={!machineSelected}>
											<InputLabel>Status</InputLabel>
											<Select
												label="Status"
												name="status"
												value={provisionFormData.status}
												onChange={handleProvisionFormChange}>
												<MenuItem value="active">Active</MenuItem>
												<MenuItem value="inactive">Inactive</MenuItem>
											</Select>
										</FormControl>
									</>
								)}
							</Box>
							{errorMessage && (
								<Typography color="error" textAlign="center" marginBottom={2}>
									{errorMessage}
								</Typography>
							)}

							<Box
								display="flex"
								flexDirection="row"
								columnGap="15px"
								alignItems="center"
								justifyContent="center">
								<Button
									variant="outlined"
									onClick={() => {
										setMachineSelected(null);
										setNewProvision(false);
									}}>
									Cancel
								</Button>
								<Button
									variant="contained"
									onClick={handleProvisionFormSubmit}
									disabled={!machineSelected}>
									{submittingProvision ? (
										<>
											<CircularProgress size={16} sx={{ color: "white", marginRight: 1 }} />{" "}
											Saving..
										</>
									) : (
										"Submit Provision"
									)}
								</Button>
							</Box>
						</Box>
					</Modal>
				</>
			)}
		</div>
	);
};

export default ManageSiteMachines;